import { type Mesh, MeshBuilder, Vector3 } from "@babylonjs/core";
import { Btn, HeroId, Slot, hv, pk } from "@rivalwatch/shared";
import { baked } from "../../audio/bake.ts";
import * as loops from "../../audio/cues/loops.ts";
import type { Cue } from "../../audio/engine.ts";
import type { Theme } from "../../audio/theory.ts";
import { bell, click, fm, later, mallet, noise, thump, trim, whoosh } from "../../audio/synth.ts";
import { glow } from "../../game/materials.ts";
import type { ClientHero, ProjectileFrame } from "../types.ts";

const V = { auraTarget: 0, skate: 1, iceArts: 2, ultMode: 3 } as const;
const PK = { Ice: pk(HeroId.LunaSnowcone, 0), Freeze: pk(HeroId.LunaSnowcone, 1), IceArts: pk(HeroId.LunaSnowcone, 2) } as const;
const hz = (semi: number, base = 293.66) => base * 2 ** (semi / 12);

interface Gear {
  mic: Mesh;
  boom: Mesh;
  skirt: Mesh;
  headphones: Mesh;
  skateL: Mesh;
  skateR: Mesh;
  rink: Mesh;
}

const iceShot: Cue = baked(
  "fire:luna-snowcone:ice-polish",
  () =>
    trim(9.8, (o) => {
      click(o, 0.18, 6400, 0.003);
      [0, 4, 9].forEach((s, i) => bell(later(o, i * 0.015), hz(24 + s), 0.12, 0.32));
      whoosh(o, 0.2, 1800, 5200, 0.12);
      return 0.28;
    }),
  { variants: 5, jitter: 0.012 },
);
const brainFreeze: Cue = trim(4.5, (o) => {
  thump(o, 0.28, 180, 70, 0.16);
  fm(o, { f: hz(31), ratio: 2.5, index: 2, index1: 0.2, gain: 0.1, d: 0.35 });
  noise(o, { color: "white", type: "highpass", f: 4500, gain: 0.16, d: 0.22 });
  return 0.6;
});
const encoreIcicles: Cue = trim(9.2, (o) => {
  for (let i = 0; i < 4; i++) bell(later(o, i * 0.04), hz(19 + i * 2), 0.14, 0.42);
  whoosh(o, 0.24, 1200, 4200, 0.28);
  return 0.7;
});
const finaleOn: Cue = trim(1.9, (o) => {
  thump(o, 0.42, 100, 48, 0.25);
  [0, 4, 7, 12].forEach((s, i) => mallet(later(o, i * 0.07), hz(s + 12), 0.18, 0.72));
  noise(o, { color: "pink", type: "bandpass", f: 1600, f1: 3600, q: 1.5, gain: 0.24, a: 0.08, d: 0.45 });
  return 1.1;
});
const lunaReload = (seconds: number): Cue =>
  baked("reload:luna-snowcone:polish", () =>
    trim(16.6, (o) => {
      click(o, 0.08, 2600, 0.006, seconds * 0.22);
      bell(later(o, seconds * 0.55), hz(24), 0.12, 0.18);
      click(o, 0.1, 4300, 0.004, seconds * 0.82);
      return seconds + 0.2;
    }),
  );
const lunaTheme = {
  bpm: 132,
  octave: 1,
  voice: mallet,
  double: (o, f, d, v) => void bell(o, f * 2, d, v * 0.38),
  notes: [
    [0, 0.5],
    [4, 0.5],
    [7, 0.5],
    [11, 0.5],
    [12, 1],
  ],
} satisfies Theme;

const hero: ClientHero<Gear> = {
  id: HeroId.LunaSnowcone,
  bulk: 0.95,
  shape: { head: 1.05, legs: 1.12 },
  gear(g) {
    const { scene, R, H, rig, add, addWorld, glow: gglow, toon } = g;
    const mic = add(MeshBuilder.CreateCylinder("snowcone-mic", { diameter: 0.08, height: 0.5, tessellation: 10 }, scene), rig.handR, toon("#123d61"));
    mic.rotation.x = Math.PI / 2;
    mic.position.z = -0.26;
    const boom = add(MeshBuilder.CreateCylinder("snowcone-boom", { diameter: 0.025, height: 0.5, tessellation: 6 }, scene), rig.head, toon("#123d61"));
    boom.rotation.x = Math.PI / 2;
    boom.rotation.z = -0.25;
    boom.position.set(R * 0.32, 0, -R * 0.45);
    const skirt = add(MeshBuilder.CreateCylinder("ice-skirt", { diameterTop: R * 1.8, diameterBottom: R * 2.25, height: H * 0.18, tessellation: 8 }, scene), rig.torso, toon("#f8fbff"));
    skirt.position.y = -H * 0.2;
    const headphones = add(MeshBuilder.CreateTorus("headphones", { diameter: R * 1.45, thickness: 0.05, tessellation: 24 }, scene), rig.head, gglow("#b58cff", 0.5));
    headphones.rotation.z = Math.PI / 2;
    const skateL = add(MeshBuilder.CreateBox("skate-l", { width: 0.12, height: 0.045, depth: 0.52 }, scene), rig.footL, gglow("#b9f3ff", 0.8));
    skateL.position.z = -0.08;
    const skateR = add(MeshBuilder.CreateBox("skate-r", { width: 0.12, height: 0.045, depth: 0.52 }, scene), rig.footR, gglow("#b9f3ff", 0.8));
    skateR.position.z = -0.08;
    const rink = addWorld(MeshBuilder.CreateTorus("finale-ring", { diameter: 20, thickness: 0.065, tessellation: 96 }, scene), gglow("#9eeaff", 0.24));
    rink.setEnabled(false);
    return { mic, boom, skirt, headphones, skateL, skateR, rink };
  },
  animate(gear, f) {
    gear.mic.rotation.z = Math.sin(f.t * 5) * 0.15;
    gear.headphones.rotation.y = f.t * 0.3;
    gear.skateL.scaling.z = 1 + f.v[V.skate] * 0.25;
    gear.skateR.scaling.z = 1 + f.v[V.skate] * 0.25;
    gear.rink.setEnabled(f.ultActive);
    gear.rink.position.set(f.pos.x, f.pos.y + 0.04, f.pos.z);
    gear.rink.scaling.y = 0.03;
    return f.ultActive ? "emote-yes" : f.v[V.skate] > 0.2 ? "sprint" : undefined;
  },
  hud(c, eid) {
    return [
      { label: "Skate", value: hv(c, eid, V.skate), color: "#7fd6ff" },
      { label: "Encore", value: Math.min(1, hv(c, eid, V.iceArts) / 6), color: "#ffffff" },
      { label: hv(c, eid, V.ultMode) > 0 ? "Finale: boost" : "Finale: heal", value: hv(c, eid, V.ultMode) > 0 ? 1 : 0, color: "#9b7cff" },
    ];
  },
  lifting(c, eid, buttons, grounded) {
    return !grounded && hv(c, eid, V.skate) > 0.4 && !!(buttons & Btn.Jump);
  },
  projectiles: {
    [PK.Ice]: (scene, f: ProjectileFrame) => {
      const shard = MeshBuilder.CreateCylinder("luna-ice-shard", { diameterTop: 0.02, diameterBottom: 0.18, height: 0.65, tessellation: 5 }, scene);
      shard.material = glow(scene, "#b9f3ff", 0.86);
      shard.position.set(f.pos.x, f.pos.y, f.pos.z);
      return {
        gear: shard,
        update(m: Mesh, frame) {
          m.position.set(frame.pos.x, frame.pos.y, frame.pos.z);
          m.lookAt(new Vector3(frame.pos.x + frame.dir.x, frame.pos.y + frame.dir.y, frame.pos.z + frame.dir.z));
          m.rotation.x += Math.PI / 2;
        },
        dispose: () => shard.dispose(),
      };
    },
    [PK.Freeze]: (scene, f: ProjectileFrame) => {
      const snowball = MeshBuilder.CreateSphere("luna-freeze-ball", { diameter: 0.58, segments: 12 }, scene);
      const ring = MeshBuilder.CreateTorus("luna-freeze-ring", { diameter: 0.72, thickness: 0.035, tessellation: 24 }, scene);
      snowball.material = glow(scene, "#d7fbff", 0.78);
      ring.material = glow(scene, "#ffffff", 0.55);
      ring.parent = snowball;
      snowball.position.set(f.pos.x, f.pos.y, f.pos.z);
      return {
        gear: { snowball, ring },
        update(g: { snowball: Mesh; ring: Mesh }, frame) {
          g.snowball.position.set(frame.pos.x, frame.pos.y, frame.pos.z);
          g.ring.rotation.y += frame.dt * 8;
        },
        dispose: () => {
          ring.dispose();
          snowball.dispose();
        },
      };
    },
    [PK.IceArts]: (scene, f: ProjectileFrame) => {
      const shard = MeshBuilder.CreateCylinder("luna-ice-arts-shard", { diameterTop: 0.08, diameterBottom: 0.34, height: 1.0, tessellation: 5 }, scene);
      shard.material = glow(scene, "#cfffff", 0.72);
      shard.position.set(f.pos.x, f.pos.y, f.pos.z);
      return {
        gear: shard,
        update(m: Mesh, frame) {
          m.position.set(frame.pos.x, frame.pos.y, frame.pos.z);
          m.lookAt(new Vector3(frame.pos.x + frame.dir.x, frame.pos.y + frame.dir.y, frame.pos.z + frame.dir.z));
          m.rotation.x += Math.PI / 2;
        },
        dispose: () => shard.dispose(),
      };
    },
  },
  zones: {
    // Luna's ultimate is avatar-owned, but this view gives her deployable finale-style rings if future forms expose one.
  },
  icons: {
    [Slot.Primary]: "M4 12l5-5 6 10 5-5M9 7l6 10",
    [Slot.Secondary]: "M12 4v16M5 8l14 8M19 8 5 16",
    [Slot.Ability1]: "M4 18c4-8 12-8 16 0M8 10h8",
    [Slot.Ability2]: "M5 12c4-5 10-5 14 0M12 6v12",
    [Slot.Ultimate]: "M12 3v18M3 12h18M6 6l12 12M18 6 6 18",
    passive: "M5 17c5 3 9 3 14 0M8 14l2 3M16 14l-2 3",
  },
  audio: {
    weight: "light",
    fire: () => ({ key: "fire:luna-snowcone:ice-polish", cue: iceShot }),
    reload: lunaReload,
    ability: { [Slot.Secondary]: brainFreeze, [Slot.Ability1]: encoreIcicles, [Slot.Ultimate]: finaleOn },
    theme: lunaTheme,
    themeTrim: { heroic: 2.2, hostile: -6.4, tease: 4.6 },
    flight: { [PK.Ice]: loops.flight("orb"), [PK.Freeze]: loops.flight("orb"), [PK.IceArts]: loops.flight("orb") },
    loops: ({ v, ultActive, alive }) => (alive && ultActive ? [{ key: v[V.ultMode] > 0 ? "finale-boost" : "finale-heal", build: loops.amplifier, gain: 0.85 }] : []),
  },
};

export default hero;
